/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Text } from '@/shared/ui';
import { ImageIcon } from '@/shared/ui/radix-icons';
import { normalizeImageUrl } from '@/shared/utils/normalize-image-url';
import { propertyAgencyCode, propertyBusinessType } from '@/shared/utils/property-display';
import type { PropertyResponseType } from '@services/housien/types';
import { StatusBadge } from './StatusBadge';
import { BusinessTypeBadge } from './BusinessTypeBadge';
import { getCategoryLabel } from './PropertyFormDualPane/schema';
import { cardPriceLabel, photoCount, specLine } from './property-card-helpers';

/** Narrowest a card gets before the grid drops a column. */
const CARD_MIN_WIDTH = 240;

interface PropertyGridViewProps {
  properties: PropertyResponseType[];
  onCardClick: (property: PropertyResponseType) => void;
  t: (key: string) => string;
}

/**
 * The portfolio as a grid of photo-first cards.
 *
 * The list is for comparing figures across rows; the grid is for recognising
 * a listing by its photo. So the photo leads at a fixed 4:3 and the text sits
 * under it, the price pinned to the bottom so every card in a row ends its
 * price on the same line however long the headline runs.
 *
 * The photo is `media.cover_card`, the 480px derivative the page asks for
 * with `?cover=card` while this view is on: the 160px row preview would be
 * visibly soft at this size.
 */
export const PropertyGridView: React.FC<PropertyGridViewProps> = ({ properties, onCardClick, t }) => {
  const { t: tLocal } = useTranslation('features/properties');

  return (
    <div
      data-testid="property-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}px, 1fr))`,
        gap: 'var(--space-4)',
      }}
    >
      {properties.map((property) => (
        <GridCard
          key={property.id}
          property={property}
          onClick={() => onCardClick(property)}
          priceLabel={cardPriceLabel(property, t)}
          specs={specLine(property, tLocal) || getCategoryLabel(property.classification?.category ?? '', t) || '—'}
          photoLabel={tLocal('list.card.photos', { count: photoCount(property) })}
          noTitle={t('list.noTitle')}
        />
      ))}
    </div>
  );
};

interface GridCardProps {
  property: PropertyResponseType;
  onClick: () => void;
  priceLabel: string;
  specs: string;
  photoLabel: string;
  noTitle: string;
}

/** One listing of the grid: photo on top, identity, figures, then place. */
const GridCard: React.FC<GridCardProps> = ({ property, onClick, priceLabel, specs, photoLabel, noTitle }) => {
  const status = property.classification?.status;
  const place = [property.location?.neighborhood, property.location?.city].filter(Boolean).join(', ');

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid="property-grid-card"
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      className="hover:shadow-(--shadow-md) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--seed-base)"
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface-primary)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-sm)',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'var(--transition-standard)',
        fontFamily: 'var(--font-family)',
      }}
    >
      <CardCover property={property} label={photoLabel} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1-5)', padding: 'var(--space-3)', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1-5)', flexWrap: 'wrap' }}>
          <Text
            as="span"
            data-testid="property-grid-card-code"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: 'var(--space-1) var(--space-2)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--surface-tertiary)',
              color: 'var(--text-secondary)',
              fontVariantNumeric: 'tabular-nums',
              fontSize: 'var(--text-meta)',
              fontWeight: 'var(--font-semibold)' as unknown as number,
              letterSpacing: 'var(--tracking-tight)',
            }}
          >
            {propertyAgencyCode(property as unknown as Record<string, unknown>) ?? property.id}
          </Text>
          <BusinessTypeBadge type={propertyBusinessType(property as unknown as Record<string, unknown>)} />
          <StatusBadge status={status === 'inactive' ? 'inactive' : 'active'} />
        </div>

        <Text
          as="span"
          style={{
            fontSize: 'var(--text-body-sm)',
            fontWeight: 'var(--font-semibold)' as unknown as number,
            color: 'var(--text-primary)',
            lineHeight: 'var(--leading-snug)',
          }}
        >
          {property.description?.headline || noTitle}
        </Text>

        <Text as="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-secondary)' }}>
          {specs}
        </Text>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-0-5)', marginTop: 'auto', paddingTop: 'var(--space-1)' }}>
          <Text
            as="span"
            style={{
              fontSize: 'var(--text-body)',
              fontWeight: 'var(--font-bold)' as unknown as number,
              color: 'var(--seed-base)',
            }}
          >
            {priceLabel}
          </Text>
          {place && (
            <Text as="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}>
              {place}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * The card's photo at 4:3, with the photo count on its bottom-left corner.
 *
 * A listing without a photo keeps the same box: every card in a row starts
 * its text at the same height, and the empty box says something true.
 */
const CardCover: React.FC<{ property: PropertyResponseType; label: string }> = ({ property, label }) => {
  const [failed, setFailed] = React.useState(false);
  const media = property.media as { cover_card?: string; cover_thumbnail?: string } | undefined;
  const url = media?.cover_card ?? media?.cover_thumbnail;
  const count = photoCount(property);

  return (
    <div
      style={{
        position: 'relative',
        aspectRatio: '4 / 3',
        background: 'var(--surface-tertiary)',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      {url && !failed ? (
        <Image
          src={normalizeImageUrl(url)}
          alt=""
          aria-hidden="true"
          loading="lazy"
          onError={() => setFailed(true)}
          // Out of flow, so a portrait photo's own height cannot stretch the
          // 4:3 box: `aspect-ratio` gives way to in-flow content.
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Text
          as="span"
          aria-hidden="true"
          className="flex h-full w-full items-center justify-center"
          style={{ color: 'var(--text-tertiary)', opacity: 0.5 }}
        >
          <ImageIcon width={28} height={28} />
        </Text>
      )}

      {count > 0 && (
        <Text
          as="span"
          data-testid="property-grid-card-photo-count"
          style={{
            position: 'absolute',
            left: 'var(--space-2)',
            bottom: 'var(--space-2)',
            padding: '1px 6px',
            borderRadius: 'var(--radius-sm)',
            background: 'color-mix(in srgb, var(--surface-inverse) 72%, transparent)',
            color: 'var(--text-inverse)',
            fontSize: 'var(--text-eyebrow)',
            lineHeight: 1.4,
            fontWeight: 'var(--font-semibold)' as unknown as number,
          }}
        >
          {label}
        </Text>
      )}
    </div>
  );
};
